import { useEffect, useState } from 'react'
import { fetchOrderHistory } from '../api'
import { clock, dolumEtiketi, price as fmtPrice, qty } from '../format'
import { durumBilgisi } from '../orderStatus'
import type { OrderFillRow, OrderHistory, OrderVersion } from '../types'

/**
 * Bir emrin hayatı — `ⓘ` ikonuna basınca açılır.
 *
 * **Tek bir zaman çizelgesi.** Önceki tasarımda aynı olay üç ayrı tabloda
 * tekrar ediyordu (sürüm satırı + dolum satırı + `order/filled` olayı hep aynı
 * şey), üstelik tablolar dar modala sığmayıp sütunları kırpılıyordu. Artık her
 * olay tek satır: ne olduğu, hangi değerlerle ve bir öncekine göre ne değiştiği.
 *
 * Sürümler ve dolumlar **sırayla eşleştiriliyor**: motor her dolumda emrin
 * sürümünü bir arttırıp bir `Fill` üretiyor, yani `reason='filled'` sürümlerle
 * dolumlar birebir ve aynı sırada. Zaman damgasına göre eşleştirmek iki dolum
 * aynı saniyeye düştüğünde yanılırdı.
 *
 * Ham olay günlüğü katlanmış duruyor: tanılama içindir, hikâyeyi anlatmaz.
 */

interface Props {
  orderId: string
  onClose: () => void
}

interface TimelineEntry {
  ts: string
  kind: 'created' | 'amended' | 'clipped' | 'filled' | 'cancelled' | 'expired' | 'other'
  title: string
  /** Ana satır — olayın kendi sayıları. */
  detail: string
  /** İkincil satır — bağlam (tetikleyen işlem, kalan miktar). */
  note?: string
  actor: string | null
}

const BASLIK: Record<string, string> = {
  created: 'Emir açıldı',
  amended: 'Düzenlendi',
  filled: 'Dolum',
  cancelled: 'İptal edildi',
  expired: 'Süresi doldu',
  clipped: 'Çapraz kırpma',
}

/** ISO zaman damgası → `clock()` biçimi. Sunucu ISO string gönderiyor. */
function an(iso: string): string {
  return clock(new Date(iso).getTime() / 1000)
}

/** İki an arası — "2sa 14dk" / "3dk 8sn" / "12sn". */
function sure(baslangic: number, bitis: number): string {
  const sn = Math.max(0, Math.round((bitis - baslangic) / 1000))
  const sa = Math.floor(sn / 3600)
  const dk = Math.floor((sn % 3600) / 60)
  if (sa) return `${sa}sa ${dk}dk`
  if (dk) return `${dk}dk ${sn % 60}sn`
  return `${sn}sn`
}

/**
 * Sürümleri ve dolumları tek bir kronolojik anlatıya çevirir.
 *
 * Düzenleme satırında eski→yeni farkı **bir önceki sürümden** hesaplanıyor,
 * sunucunun `note` metninden değil: not ham sayı biçiminde geliyor
 * (`1234.5×20.0`), burada yerel biçimle yazmak istiyoruz.
 */
function buildTimeline(versions: OrderVersion[], fills: OrderFillRow[]): TimelineEntry[] {
  let fillIndex = 0
  return versions.map((v, i) => {
    const onceki = i > 0 ? versions[i - 1] : undefined
    const ortak = { ts: v.ts, actor: v.actor, title: BASLIK[v.reason] ?? v.reason }

    if (v.reason === 'filled') {
      const fill = fills[fillIndex++]
      const kalan = v.quantity - v.filledQuantity
      const sapma = fill ? Math.abs(fill.price - fill.tradePrice) : 0
      // Kaynağa göre anlatı: `trade` gerçek bir işlem baskısıdır, tahta
      // kaynaklılarda ortada basılmış bir piyasa işlemi YOK — "piyasa işlem
      // gördü" yazmak yanlış olurdu.
      const kaynakNotu = !fill
        ? ''
        : fill.liquidity === 'sweep'
          ? `tahtadaki karşı emir ${fmtPrice(fill.tradePrice)} fiyatla vuruldu`
          : fill.liquidity === 'book'
            ? `karşı emir ${fmtPrice(fill.tradePrice)} ile üstümüze geldi`
            : `piyasa ${fmtPrice(fill.tradePrice)}'den işlem gördü` +
              (sapma > 0 ? ` · lehimize ${fmtPrice(sapma)} fark` : '')
      return {
        ...ortak,
        kind: 'filled',
        detail: fill
          ? `${qty(fill.quantity)} lot @ ${fmtPrice(fill.price)} · ${dolumEtiketi(fill.liquidity)}`
          : `${qty(v.filledQuantity)} lot doldu`,
        note: kaynakNotu + (kalan > 0 ? ` · kalan ${qty(kalan)} lot` : ' · emir tamamlandı'),
      }
    }

    if ((v.reason === 'amended' || v.reason === 'clipped') && onceki) {
      const fiyatDegisti = onceki.price !== v.price
      const miktarDegisti = onceki.quantity !== v.quantity
      const parcalar: string[] = []
      if (fiyatDegisti) parcalar.push(`${fmtPrice(onceki.price)} → ${fmtPrice(v.price)}`)
      if (miktarDegisti) {
        parcalar.push(`${qty(onceki.quantity)} → ${qty(v.quantity)} lot`)
      }
      return {
        ...ortak,
        kind: v.reason === 'clipped' ? 'clipped' : 'amended',
        detail: parcalar.join('  ·  '),
        note:
          v.reason === 'clipped'
            ? 'karşı tarafı vuracaktı, otomatik pasife çekildi'
            : fiyatDegisti
              ? `fiyat ${v.price > onceki.price ? 'yükseltildi' : 'düşürüldü'}`
              : 'miktar değişti',
      }
    }

    if (v.reason === 'created') {
      return {
        ...ortak,
        kind: 'created',
        detail: `${fmtPrice(v.price)} × ${qty(v.quantity)} lot`,
        note: v.side === 'BUY' ? 'alış' : 'satış',
      }
    }

    // cancelled / expired / bilinmeyen
    return {
      ...ortak,
      kind: (v.reason === 'cancelled' || v.reason === 'expired' ? v.reason : 'other') as
        TimelineEntry['kind'],
      detail: `${fmtPrice(v.price)} × ${qty(v.quantity)} lot`,
      note:
        v.filledQuantity > 0
          ? `${qty(v.filledQuantity)} lot dolmuştu, kalanı düştü`
          : 'hiç dolmadan kapandı',
    }
  })
}

export function OrderLogModal({ orderId, onClose }: Props) {
  const [history, setHistory] = useState<OrderHistory | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const [hamGunluk, setHamGunluk] = useState(false)

  useEffect(() => {
    let iptal = false
    fetchOrderHistory(orderId)
      .then((h) => !iptal && setHistory(h))
      .catch((err) => !iptal && setHata(err instanceof Error ? err.message : 'Geçmiş alınamadı'))
    return () => {
      iptal = true
    }
  }, [orderId])

  // Esc ile kapanma: modal açıkken tek çıkış yolu fare olmasın.
  useEffect(() => {
    const kapat = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', kapat)
    return () => window.removeEventListener('keydown', kapat)
  }, [onClose])

  const o = history?.order
  const durum = o ? durumBilgisi(String(o.status)) : null
  const timeline = history ? buildTimeline(history.versions, history.fills) : []

  const dolan = o ? Number(o.filledQuantity) : 0
  const toplam = o ? Number(o.quantity) : 0
  const oran = toplam > 0 ? Math.min(100, (dolan / toplam) * 100) : 0

  // Dolumların ortalaması: birden çok dolumda tek fiyat yanıltıcı olurdu.
  const ortalama =
    history && history.fills.length
      ? history.fills.reduce((s, f) => s + f.price * f.quantity, 0) /
        history.fills.reduce((s, f) => s + f.quantity, 0)
      : null

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      {/* Gövdeye tıklamak kapatmamalı; yalnız arka plan kapatır. */}
      <div
        className="order-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${orderId} emir geçmişi`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <span className="modal-title">
            <code>{orderId}</code>
            {o && (
              <span className="muted">
                {' · '}
                {String(o.contract)} · {o.side === 'BUY' ? 'Alış' : 'Satış'}
              </span>
            )}
          </span>
          {durum && (
            <span className={`durum-etiket ${durum.tone}`}>
              <span className={`durum-rozet ${durum.tone}`}>{durum.code}</span>
              {durum.label}
            </span>
          )}
          <button type="button" className="modal-close" onClick={onClose} aria-label="Kapat">
            ×
          </button>
        </div>

        <div className="modal-body">
          {hata && <div className="order-error">{hata}</div>}
          {!history && !hata && <div className="shadow-empty">Yükleniyor…</div>}

          {history && o && (
            <>
              <div className="ozet">
                <div className="ozet-kutu">
                  <span>Hacim</span>
                  <b>
                    {qty(dolan)}/{qty(toplam)} lot
                  </b>
                  {/* Dolum oranı çubuğu: "ne kadarı gerçekleşti" tek bakışta. */}
                  <span className="ozet-bar">
                    <span className={`ozet-bar-fill ${durum?.tone ?? ''}`} style={{ width: `${oran}%` }} />
                  </span>
                </div>

                <div className="ozet-kutu">
                  <span>Son fiyat</span>
                  <b>{fmtPrice(Number(o.price))}</b>
                  {ortalama != null && Math.abs(ortalama - Number(o.price)) > 0.001 && (
                    <span className="ozet-alt">ort. dolum {fmtPrice(ortalama)}</span>
                  )}
                </div>

                <div className="ozet-kutu">
                  <span>Açılış</span>
                  <b>{clock(Number(o.createdAt))}</b>
                  <span className="ozet-alt">
                    {sure(Number(o.createdAt) * 1000, Number(o.updatedAt) * 1000)} sürdü
                  </span>
                </div>

                <div className="ozet-kutu">
                  <span title="Emir açıldığı anda o fiyat seviyesinde duran gerçek miktar. Eşleşmede kullanılmaz, yalnız kayıt.">
                    Açılışta önündeki
                  </span>
                  <b>{o.queueAhead == null ? '–' : `${qty(Number(o.queueAhead))} lot`}</b>
                  <span className="ozet-alt">sürüm {String(o.version)}</span>
                </div>
              </div>

              <div className="modal-section">Ne oldu?</div>
              <ol className="zaman-cizelgesi">
                {timeline.map((e, i) => (
                  <li className={`zaman-olay ${e.kind}`} key={`${e.ts}-${i}`}>
                    <span className="zaman-nokta" aria-hidden="true" />
                    <span className="zaman-saat">{an(e.ts)}</span>
                    <span className="zaman-govde">
                      <span className="zaman-baslik">
                        {e.title}
                        <span className="zaman-detay">{e.detail}</span>
                      </span>
                      {e.note && <span className="zaman-not">{e.note}</span>}
                    </span>
                    <span className="zaman-kaynak">{e.actor ?? ''}</span>
                  </li>
                ))}
              </ol>

              {/* Ham günlük tanılama içindir; hikâyeyi zaten yukarısı anlatıyor. */}
              <button
                type="button"
                className="ham-ac"
                onClick={() => setHamGunluk((v) => !v)}
                aria-expanded={hamGunluk}
              >
                {hamGunluk ? '▾' : '▸'} Ham olay günlüğü ({history.events.length})
              </button>
              {hamGunluk && (
                <div className="ham-gunluk">
                  {history.events.map((e, i) => (
                    <div className={`ham-satir ${e.severity}`} key={`${e.ts}-${i}`}>
                      <span className="muted">{an(e.ts)}</span>
                      <span>
                        {e.category}/{e.action}
                      </span>
                      <code>{JSON.stringify(e.payload)}</code>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
